import { Component } from 'react'
import { toast } from 'react-toastify'
import Loader from '../../../components/loader'
import { getLocale, getString } from '../../../locale'
import { getSyrupUsageList, replaceSyrupUsage, SyrupUsageEntry } from '../../../api/0x0005/drinks'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    replacingId?: number
    entries: SyrupUsageEntry[]
}

export default class SyrupUsageTab extends Component<Props, State> {
    state: State = {
        loading: true,
        replacingId: undefined,
        entries: [],
    }

    componentDidMount() {
        this.load()
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const response = await getSyrupUsageList(this.props.clientId)
            this.setState({ entries: response.data.entries || [], loading: false })
        } catch (error: any) {
            toast.error(error.message || getString('syrup_usage.load_error'))
            this.setState({ loading: false })
        }
    }

    replace = async (entry: SyrupUsageEntry) => {
        this.setState({ replacingId: entry.syrupId })
        try {
            const response = await replaceSyrupUsage(this.props.clientId, entry.syrupId)
            if (response.data.success === 0) {
                throw new Error(response.data.errorMsg || getString('syrup_usage.replace_error'))
            }
            toast.success(getString('syrup_usage.replace_success'))
            await this.load()
        } catch (error: any) {
            toast.error(error.message || getString('syrup_usage.replace_error'))
            this.setState({ replacingId: undefined })
        }
    }

    render() {
        const { loading, entries, replacingId } = this.state
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{getString('syrup_usage.title')}</h1>
                    <button
                        onClick={this.load}
                        className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {getString('settings.refresh')}
                    </button>
                </div>

                {entries.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 text-gray-600 dark:text-gray-300">
                        {getString('syrup_usage.empty')}
                    </div>
                )}

                {entries.map(entry => (
                    <div key={entry.syrupId} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {entry.syrupName} <span className="text-sm text-gray-500">#{entry.syrupId}</span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {getString('syrup_usage.channel')}: {entry.linkedChannelNum}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-3 text-sm text-gray-700 dark:text-gray-300">
                            <div>
                                {getString('syrup_usage.used_ml')}: {entry.usedMl.toFixed(1)}
                            </div>
                            <div>
                                {getString('syrup_usage.remaining_ml')}: {entry.remainingMl.toFixed(1)}
                            </div>
                            <div>
                                {getString('syrup_usage.package_l')}: {entry.packageSizeLiters.toFixed(2)}
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-300 mb-1">
                                <span>{getString('syrup_usage.remaining')}</span>
                                <span>{entry.remainingPercent}%</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-500"
                                    style={{ width: `${Math.max(0, Math.min(100, entry.remainingPercent))}%` }}
                                />
                            </div>
                        </div>

                        <div className={getLocale() === 'he' ? 'text-left' : 'text-right'}>
                            <button
                                onClick={() => this.replace(entry)}
                                disabled={replacingId === entry.syrupId}
                                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white"
                            >
                                {replacingId === entry.syrupId ? getString('common.loading') : getString('syrup_usage.replace')}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )
    }
}
